import React from "react";
import { Shield, Plus, Trash2, ChevronLeft, ChevronRight, Phone, MessageSquare, Check } from "lucide-react";
import { cn } from "@hts/ui";
import { useGet, apiPost, apiDelete } from "../../hooks/useApi.js";
import { CardSkeleton } from "../../components/ui/Skeleton.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import type { OnCallSlot, Technician } from "../../api/types.js";

export function OnCallPage() {
  const { data, loading, refetch } = useGet<{ items: OnCallSlot[] }>("/api/v1/oncall");
  const { data: techData } = useGet<{ items: Technician[] }>("/api/v1/technicians");
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  const [notifying, setNotifying] = React.useState<string | null>(null);
  const [notified, setNotified] = React.useState<Set<string>>(new Set());

  const slots = data?.items ?? [];
  const techs = techData?.items ?? [];

  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay() + 1 + weekOffset * 7);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  function slotForDay(day: Date) {
    const dayStr = day.toDateString();
    return slots.filter(s => {
      const start = new Date(s.startsAt);
      return start.toDateString() === dayStr;
    });
  }

  async function deleteSlot(id: string) {
    setDeleting(id);
    try { await apiDelete("/api/v1/oncall/" + id); refetch(); }
    finally { setDeleting(null); }
  }

  async function notifySlot(id: string) {
    setNotifying(id);
    try {
      const res = await fetch("/api/v1/oncall/" + id + "/notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const d = await res.json();
      if (d.ok) {
        setNotified(prev => new Set([...prev, id]));
        setTimeout(() => setNotified(prev => { const n = new Set(prev); n.delete(id); return n; }), 3000);
      }
    } finally { setNotifying(null); }
  }

  const ROTATION_COLORS: Record<string, string> = {
    Primary: "bg-brand-100 text-brand-700 border-brand-300 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-700",
    Secondary: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700",
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">On-Call Roster</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">Manage on-call rotations and after-hours coverage</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors shadow-sm">
          <Plus className="h-4 w-4" /> Add slot
        </button>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
        <button onClick={() => setWeekOffset(w => w - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold text-[hsl(var(--content))]">
            {weekDays[0]?.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – {weekDays[6]?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>
          {weekOffset === 0 && <p className="text-xs text-brand-600 dark:text-brand-400">Current week</p>}
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-7 gap-2">{Array.from({ length: 7 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {weekDays.map(day => {
            const daySlots = slotForDay(day);
            const isToday = day.toDateString() === today.toDateString();
            return (
              <div key={day.toDateString()} className={cn("rounded-xl border p-3 min-h-[160px] space-y-2", isToday ? "border-brand-400 bg-brand-50 dark:bg-brand-900/10" : "border-[hsl(var(--border))] bg-[hsl(var(--surface))]")}>
                <div className="text-center pb-2 border-b border-[hsl(var(--border))]">
                  <p className="text-[10px] font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">{day.toLocaleDateString("en-US", { weekday: "short" })}</p>
                  <p className={cn("text-lg font-bold", isToday ? "text-brand-600 dark:text-brand-400" : "text-[hsl(var(--content))]")}>{day.getDate()}</p>
                </div>
                {daySlots.length === 0 ? (
                  <p className="text-[10px] text-[hsl(var(--content-placeholder))] text-center italic">No coverage</p>
                ) : (
                  daySlots.map(slot => (
                    <div key={slot.id} className={cn("rounded-lg border px-2 py-1.5 text-[10px] font-medium group relative", ROTATION_COLORS[slot.rotation] ?? "bg-gray-100 text-gray-600 border-gray-300")}>
                      <div className="font-semibold truncate">{slot.techName?.split(" ")[0]}</div>
                      <div className="opacity-70">{slot.rotation}</div>
                      {slot.techMobile && (
                        <div className="flex items-center gap-0.5 opacity-70 mt-0.5">
                          <Phone className="h-2.5 w-2.5" />
                          <span className="text-[9px]">{slot.techMobile}</span>
                        </div>
                      )}
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {slot.techMobile && (
                          <button onClick={() => notifySlot(slot.id)} disabled={notifying === slot.id} className="p-0.5 rounded hover:bg-white/50" aria-label="Send SMS notification" title="Send SMS notification">
                            {notifying === slot.id ? <Check className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                          </button>
                        )}
                        <button onClick={() => deleteSlot(slot.id)} disabled={deleting === slot.id} className="p-0.5 rounded hover:bg-white/50" aria-label="Delete slot">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      {notified.has(slot.id) && <p className="text-[9px] text-green-600 mt-0.5">Notified!</p>}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-[hsl(var(--content-subtle))]">
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-brand-200 border border-brand-300" /> Primary rotation</div>
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-purple-200 border border-purple-300" /> Secondary rotation</div>
        <div className="flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Click to send SMS notification</div>
      </div>

      {addOpen && (
        <AddSlotDialog
          techs={techs}
          onClose={() => setAddOpen(false)}
          onSave={async (slot) => { await apiPost("/api/v1/oncall", slot); refetch(); setAddOpen(false); }}
        />
      )}
    </div>
  );
}

function AddSlotDialog({ techs, onClose, onSave }: { techs: Technician[]; onClose: () => void; onSave: (slot: { techId: string; startsAt: string; endsAt: string; rotation: string }) => Promise<void> }) {
  const [techId, setTechId] = React.useState(techs[0]?.id ?? "");
  const [startsAt, setStartsAt] = React.useState("");
  const [endsAt, setEndsAt] = React.useState("");
  const [rotation, setRotation] = React.useState("Primary");
  const [saving, setSaving] = React.useState(false);

  async function submit() {
    if (!techId || !startsAt || !endsAt) return;
    setSaving(true);
    try { await onSave({ techId, startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(), rotation }); }
    finally { setSaving(false); }
  }

  React.useEffect(() => {
    function handler(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl p-6 space-y-4 animate-scale-in">
        <h3 className="text-base font-bold text-[hsl(var(--content))]">Add On-Call Slot</h3>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">Technician</span>
            <select value={techId} onChange={e => setTechId(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-sm text-[hsl(var(--content))] px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500">
              {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">Starts at</span>
            <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-sm text-[hsl(var(--content))] px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">Ends at</span>
            <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-sm text-[hsl(var(--content))] px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">Rotation</span>
            <select value={rotation} onChange={e => setRotation(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-sm text-[hsl(var(--content))] px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500">
              <option value="Primary">Primary</option>
              <option value="Secondary">Secondary</option>
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] border border-[hsl(var(--border))] rounded-xl transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving || !techId || !startsAt || !endsAt} className="px-4 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-xl disabled:opacity-50 transition-colors">{saving ? "Saving…" : "Add slot"}</button>
        </div>
      </div>
    </div>
  );
}
